/* eslint-disable @typescript-eslint/no-explicit-any */
import { PATHS } from "@/router/routes"
import { PayloadFormatter } from "@/utils/format"
import { createLogger } from "@/utils/logger"
import { useCallback, useMemo } from "react"
import { Navigate, useLocation, useMatch, useNavigate, useSearchParams } from "react-router-dom"
import { wait } from "@/utils/common"
import { Auth } from "@/features_signal/auth"
import { signal } from "@preact/signals"
import { ApiSignalFetchers, BusinessSignals } from "@/apis/api_signals"

const logger = createLogger("useSearchAction")

const callbackSignal = signal<(() => Promise<void>) | null>(null)

export function useSearchAction() {
    const [searchParams, setSearchParams] = useSearchParams()
    const isUnderAction = useMemo(() => {
        return !!searchParams.get("action")
    }, [searchParams])

    const clearAction = useCallback(() => {
        if (!isUnderAction) {
            return
        }

        setSearchParams(
            (prev) => {
                const newParams = new URLSearchParams(prev)
                newParams.delete("action")
                newParams.delete("payload")
                return newParams
            },
            { replace: true },
        )
    }, [isUnderAction, setSearchParams])

    const setAction = useCallback(
        (action: SearchAction.Action, payload?: Record<string, any>) => {
            if (isUnderAction) {
                return clearAction()
            }

            setSearchParams((prev) => {
                const newParams = new URLSearchParams(prev)
                newParams.set("action", action)
                if (payload) {
                    newParams.set("payload", PayloadFormatter.encode(payload))
                }
                return newParams
            })
        },
        [clearAction, isUnderAction, setSearchParams],
    )

    const action = searchParams.get("action")
    const payload = searchParams.get("payload")

    const parsedPayload = payload ? PayloadFormatter.decode(payload) : null

    const NavigateAction = useCallback(
        (action: SearchAction.Action, payload?: Record<string, any>) => {
            const searchString = new URLSearchParams()
            searchString.set("action", action)
            if (payload) {
                searchString.set("payload", PayloadFormatter.encode(payload))
            }

            return (
                <Navigate
                    to={{
                        pathname: PATHS.LOGIN,
                        search: searchString.toString(),
                    }}
                    replace
                />
            )
        },
        [],
    )

    return {
        action,
        payload: parsedPayload,
        setAction,
        clearAction,
        NavigateAction,
    }
}

type LoginStep = "login-otp-1" | "login-otp-2" | "login-password" | "login-success"

export function useLoginAction() {
    const location = useLocation()
    const navigate = useNavigate()
    const {
        //
        setAction,
        clearAction,
    } = useSearchAction()

    type PayloadData = {
        otpId?: string
        countryCode?: string
        number?: string
    }

    const [searchParams, setSearchParams] = useSearchParams({
        payload: PayloadFormatter.encode({
            step: "login-otp-1",
        }),
    })

    const mode = useMemo<"modal" | "page" | null>(() => {
        return searchParams.get("action") === "login" ? "modal" : "page"
    }, [searchParams])

    const openModalToStep = useCallback(
        (step: LoginStep, data?: PayloadData) => {
            setAction("login", { step, data })
        },
        [setAction],
    )

    const payload = useMemo<{
        step: LoginStep
        data?: PayloadData
    }>(() => {
        try {
            const payload = PayloadFormatter.decode(searchParams.get("payload") || "{}")
            return payload as {
                step: LoginStep
                data?: PayloadData
            }
        } catch (error) {
            return {
                step: "login-otp-1",
                data: {},
            }
        }
    }, [searchParams])

    const step = useMemo(() => {
        try {
            return payload.step
        } catch (error) {
            return "login-otp-1" as LoginStep
        }
    }, [payload.step])

    const moveToStep = useCallback(
        (step: LoginStep, data?: PayloadData) => {
            setSearchParams(
                (prev) => {
                    const newParams = new URLSearchParams(prev)
                    const action = newParams.get("action")
                    if (action) {
                        newParams.set("action", action)
                    }
                    newParams.set("payload", PayloadFormatter.encode({ step, data }))
                    return newParams
                },
                {
                    replace: true,
                },
            )
        },
        [setSearchParams],
    )

    const setPayload = useCallback(
        (data: PayloadData) => {
            setSearchParams(
                (prev) => {
                    const newParams = new URLSearchParams(prev)
                    newParams.set("payload", PayloadFormatter.encode({ ...payload, data }))
                    return newParams
                },
                {
                    replace: true,
                },
            )
        },
        [setSearchParams, payload],
    )

    const close = useCallback(() => {
        if (mode === "modal") {
            clearAction()
        } else {
            navigate(PATHS.INDEX)
        }
    }, [clearAction, mode, navigate])

    const loginSuccess = useCallback(async () => {
        await Promise.all([
            ApiSignalFetchers.fetchDataSignalKyc(),
            ApiSignalFetchers.fetchDataSignalProfile(),
        ])
        if (mode === "modal") {
            clearAction()
        }
        await wait(100)

        if (callbackSignal.value) {
            await callbackSignal.value()
            callbackSignal.value = null
        } else {
            const hasToGoToKyc = !(
                [
                    //
                    "under_review",
                    "block",
                    "fully_kyc",
                ] as Kyc.Status[]
            ).includes(BusinessSignals.kycSourceData.value.kyc.status)

            if (hasToGoToKyc) {
                await wait(100)
                await navigate(PATHS.KYC, {
                    replace: true,
                })
            } else {
                await navigate(
                    {
                        pathname: location.state?.from ?? PATHS.INDEX,
                        search: location.state?.search,
                    },
                    {
                        replace: true,
                    },
                )
            }
        }
    }, [mode, navigate, location.state?.from, location.state?.search, clearAction])

    return {
        //
        openModalToStep,
        moveToStep,
        step,
        payload,
        mode,
        setPayload,
        close,
        loginSuccess,
    }
}

export function useAuthRequiredExecuteCallback() {
    const isAuthenticated = Auth.isAuthenticatedSignal.value
    const { openModalToStep } = useLoginAction()

    const execute = useCallback(
        async (callback?: () => Promise<void>) => {
            callbackSignal.value = callback as () => Promise<void>
            if (isAuthenticated) {
                await callback?.()
                callbackSignal.value = null
            } else {
                await openModalToStep("login-otp-1")
            }
        },
        [isAuthenticated, openModalToStep],
    )

    return execute
}
